import * as Tooltip from "@radix-ui/react-tooltip";
import { motion, useReducedMotion } from "framer-motion";
import { useFreshness } from "@/hooks/useFreshness";
import { dateSpanish } from "@/lib/format";
import { cn } from "@/lib/cn";

/** Small chip on the white header pill showing "datos hasta X".
 * role=status so screen readers announce freshness changes.
 *
 * On hover, a Radix tooltip exposes: last update, estimated next sync,
 * and a warning line when data is stale. When `stale`, the chip also
 * performs a soft horizontal shake every ~10s so it gently asks for
 * attention without being noisy. */
export function FreshnessChip() {
  const { data, isPending } = useFreshness();
  const reduced = useReducedMotion();

  if (isPending || !data) {
    return (
      <span
        role="status"
        aria-live="polite"
        className="inline-flex h-6 w-40 items-center rounded-full bg-black/5"
      >
        <span className="sr-only">Cargando frescura de datos</span>
      </span>
    );
  }

  const base =
    "inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold tabular cursor-help outline-none focus-visible:ring-2 focus-visible:ring-brand-1";
  const tone = data.stale
    ? "bg-amber-500/15 text-amber-700 ring-1 ring-inset ring-amber-500/40"
    : "bg-black/[0.04] text-slate-700 ring-1 ring-inset ring-black/[0.06]";

  const staleText = data.stale ? ` · +${data.delta_days}d de retraso` : "";

  // Shake keyframes fire once every 10s; a tiny horizontal jitter
  // draws the eye without pulling focus from the main content.
  const shakeAnimation =
    data.stale && !reduced
      ? {
          x: [0, -2, 2, -1.5, 1.5, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
        }
      : undefined;

  return (
    <Tooltip.Provider delayDuration={180} skipDelayDuration={80}>
      <Tooltip.Root>
        <Tooltip.Trigger asChild>
          <motion.span
            role="status"
            aria-live="polite"
            tabIndex={0}
            className={cn(base, tone)}
            animate={shakeAnimation}
            transition={
              data.stale && !reduced
                ? {
                    duration: 10,
                    repeat: Infinity,
                    ease: "easeInOut",
                    times: [
                      0, 0.01, 0.02, 0.03, 0.04, 0.05, 0.2, 0.4, 0.5,
                      0.6, 0.7, 0.8, 0.9, 0.95, 1,
                    ],
                  }
                : undefined
            }
          >
            <span className="h-1.5 w-1.5 rounded-full bg-current" aria-hidden />
            Datos hasta {dateSpanish(data.max_date)}
            {staleText}
          </motion.span>
        </Tooltip.Trigger>
        <Tooltip.Portal>
          <Tooltip.Content
            sideOffset={8}
            className="z-50 max-w-xs rounded-lg border border-border-2 bg-bg-1/95 px-3 py-2 text-xs leading-relaxed text-text-2 shadow-lg backdrop-blur"
          >
            <div className="font-semibold text-text-1">
              Última actualización: {dateSpanish(data.max_date)}
            </div>
            <div className="mt-1 text-text-3">
              Próxima sincronización estimada: mañana ~02:00 UTC
            </div>
            {data.stale && (
              <div className="mt-2 flex items-start gap-1.5 text-amber-400">
                <span aria-hidden>⚠️</span>
                <span>Datos con retraso, revisando pipeline</span>
              </div>
            )}
            <Tooltip.Arrow className="fill-bg-1" />
          </Tooltip.Content>
        </Tooltip.Portal>
      </Tooltip.Root>
    </Tooltip.Provider>
  );
}
